import React from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RefreshCw, Zap, GraduationCap, Plus, Building2, User } from "lucide-react";
import { adminApi } from "../lib/api";

const PENALTY_TYPES = [
  { value: "no_show", label: "Manglende vagt (no-show)", default: 500 },
  { value: "undelivered_package", label: "Pakke ikke leveret", default: 35 },
  { value: "tos_violation", label: "Vilkår-brud", default: 1000 },
  { value: "other", label: "Anden", default: 0 },
];

export default function DriverTrainingPenaltiesPanel() {
  const [drivers, setDrivers] = React.useState(null);
  const [scanning, setScanning] = React.useState(false);
  const [assigning, setAssigning] = React.useState(null);
  const [penalties, setPenalties] = React.useState({}); // {driver_id: {penalties:[], active_total_dkk}}
  const [openPenaltyForm, setOpenPenaltyForm] = React.useState(null);
  const [pf, setPf] = React.useState({ penalty_type: "no_show", amount_dkk: 500, reason: "", period: "" });

  const loadDrivers = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/drivers-performance?days=30");
      setDrivers(data.drivers || []);
      // Pre-fetch penalties for each
      const all = {};
      for (const p of (data.drivers || [])) {
        try {
          const { data: pp } = await adminApi().get(`/admin/drivers/${p.driver_id}/penalties`);
          all[p.driver_id] = pp;
        } catch { all[p.driver_id] = { penalties: [], active_total_dkk: 0 }; }
      }
      setPenalties(all);
    } catch { toast.error("Kunne ikke hente chauffører"); }
  }, []);

  React.useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const scanAutoTraining = async () => {
    setScanning(true);
    try {
      const { data } = await adminApi().post("/admin/auto-training/scan");
      toast.success(`Scan færdig: ${data.assigned_count} chauffører fik tildelt træning`);
      loadDrivers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan fejlede"); }
    finally { setScanning(false); }
  };

  const assignTraining = async (driverId, topic) => {
    setAssigning(driverId);
    try {
      await adminApi().post(`/admin/drivers/${driverId}/training/assign`, {
        driver_id: driverId, topic, reason: "manual",
      });
      toast.success("Træning tildelt — chauffør låst fra nye opgaver indtil gennemført");
      loadDrivers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke tildele"); }
    finally { setAssigning(null); }
  };

  const createPenalty = async (driverId) => {
    if (!pf.reason || pf.reason.length < 3) { toast.error("Indtast årsag"); return; }
    if (pf.amount_dkk <= 0) { toast.error("Beløb skal være > 0"); return; }
    try {
      await adminApi().post(`/admin/drivers/${driverId}/penalties`, {
        driver_id: driverId, ...pf,
      });
      toast.success(`Bøde oprettet: ${pf.amount_dkk} kr`);
      setOpenPenaltyForm(null);
      loadDrivers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke oprette bøde"); }
  };

  const waivePenalty = async (penaltyId) => {
    if (!confirm("Afskriv denne bøde?")) return;
    try {
      await adminApi().delete(`/admin/penalties/${penaltyId}`);
      toast.success("Bøde afskrevet");
      loadDrivers();
    } catch { toast.error("Kunne ikke afskrive"); }
  };

  if (!drivers) return <div className="text-brand-text2 p-6">Indlæser...</div>;

  return (
    <div className="space-y-4" data-testid="admin-training-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="kicker">Træning & Bøder</div>
          <h2 className="font-display font-bold text-2xl text-brand-dark mt-1">Service-standard & disciplin</h2>
          <p className="text-sm text-brand-text2 mt-1">Auto-tildel træning til chauffører med {">"} 5% forgæves eller manglende opkald. Manuelle bøder trækkes fra månedsopgørelsen.</p>
        </div>
        <button onClick={scanAutoTraining} disabled={scanning} className="btn-primary inline-flex items-center gap-2" data-testid="scan-auto-training">
          {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
          Scan & auto-tildel
        </button>
      </div>

      {drivers.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center">
          <User size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen aktive chauffører</div>
        </div>
      ) : (
        drivers.map((p) => {
          const pdata = penalties[p.driver_id] || { penalties: [], active_total_dkk: 0 };
          const needs = p.fail_rate > 0.05 || (p.no_home_failed > 0 && p.call_compliance_rate < 1);
          return (
            <div key={p.driver_id} className={`bg-white border ${needs ? "border-rose-300" : "border-brand"} p-4`} data-testid={`admin-tp-row-${p.driver_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-brand-dark flex items-center gap-2">
                    <User size={14} className="text-brand-red" />
                    {p.driver.name}
                    {needs && <span className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 font-bold uppercase tracking-widest"><AlertTriangle size={10} className="inline" /> Trigger</span>}
                  </div>
                  <div className="text-xs text-brand-text2 mt-1">
                    Score: {p.score !== null ? `${p.score}/100` : "—"} · Forgæves: {(p.fail_rate * 100).toFixed(1)}% · Opkalds-comp: {p.no_home_failed > 0 ? `${(p.call_compliance_rate * 100).toFixed(0)}%` : "—"}
                  </div>
                  {pdata.active_total_dkk > 0 && (
                    <div className="text-xs text-rose-700 mt-1 font-bold">⚠ Aktive bøder: {pdata.active_total_dkk.toLocaleString("da-DK")} kr</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => assignTraining(p.driver_id, "Pre-Return Calling Protocol")} disabled={assigning === p.driver_id} className="text-xs px-2 py-1 border border-amber-300 text-amber-800 hover:bg-amber-50" data-testid={`assign-call-training-${p.driver_id}`}>
                    <GraduationCap size={11} className="inline" /> Opkald
                  </button>
                  <button onClick={() => assignTraining(p.driver_id, "Customer Service 101")} disabled={assigning === p.driver_id} className="text-xs px-2 py-1 border border-amber-300 text-amber-800 hover:bg-amber-50" data-testid={`assign-cs-training-${p.driver_id}`}>
                    <GraduationCap size={11} className="inline" /> CS-101
                  </button>
                  <button onClick={() => { setOpenPenaltyForm(p.driver_id); setPf({ penalty_type: "no_show", amount_dkk: 500, reason: "", period: new Date().toISOString().slice(0, 7) }); }} className="text-xs px-2 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`open-penalty-${p.driver_id}`}>
                    <Plus size={11} className="inline" /> Bøde
                  </button>
                </div>
              </div>

              {openPenaltyForm === p.driver_id && (
                <div className="mt-3 bg-rose-50 border border-rose-200 p-3 space-y-2" data-testid={`penalty-form-${p.driver_id}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select className="input-flat" value={pf.penalty_type} onChange={(e) => {
                      const t = PENALTY_TYPES.find(x => x.value === e.target.value);
                      setPf((f) => ({ ...f, penalty_type: e.target.value, amount_dkk: t?.default ?? f.amount_dkk }));
                    }} data-testid="penalty-type">
                      {PENALTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input type="number" className="input-flat" min="1" value={pf.amount_dkk} onChange={(e) => setPf(f => ({ ...f, amount_dkk: Number(e.target.value) }))} placeholder="Beløb DKK" data-testid="penalty-amount" />
                    <input className="input-flat" value={pf.period} onChange={(e) => setPf(f => ({ ...f, period: e.target.value }))} placeholder="YYYY-MM" data-testid="penalty-period" />
                  </div>
                  <input className="input-flat w-full" value={pf.reason} onChange={(e) => setPf(f => ({ ...f, reason: e.target.value }))} placeholder="Årsag (min 3 tegn)" data-testid="penalty-reason" />
                  <div className="flex gap-2">
                    <button onClick={() => createPenalty(p.driver_id)} className="btn-primary text-xs" data-testid={`save-penalty-${p.driver_id}`}>Opret bøde</button>
                    <button onClick={() => setOpenPenaltyForm(null)} className="btn-secondary text-xs">Annullér</button>
                  </div>
                </div>
              )}

              {pdata.penalties.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-brand-text2 font-bold uppercase tracking-widest">Bøde-historik ({pdata.penalties.length})</summary>
                  <div className="mt-2 space-y-1">
                    {pdata.penalties.map(pen => (
                      <div key={pen.id} className={`flex items-center justify-between gap-2 text-xs p-2 ${pen.status === "active" ? "bg-rose-50" : "bg-slate-50 line-through opacity-60"}`}>
                        <div className="min-w-0 flex-1">
                          <span className="font-mono font-bold">{pen.amount_dkk} kr</span> · {pen.penalty_type} · {pen.period} · {pen.reason}
                          <div className="text-[10px] text-brand-text2">{new Date(pen.created_at).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" })} af {pen.created_by}</div>
                        </div>
                        {pen.status === "active" && (
                          <button onClick={() => waivePenalty(pen.id)} className="text-rose-600 hover:text-rose-800" data-testid={`waive-penalty-${pen.id}`} title="Afskriv">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
